import React, { useState, useMemo } from 'react';
import { TrendingUp, Calculator, DollarSign, Percent, ShieldCheck, HelpCircle, FileText, ChevronRight } from 'lucide-react';

export default function Deals() {
  const [arv, setArv] = useState<number>(350000);
  const [rehab, setRehab] = useState<number>(55000);
  const [fee, setFee] = useState<number>(15000);
  const [rulePercent, setRulePercent] = useState<number>(70);
  const [notes, setNotes] = useState<string>('');
  const [dealAddress, setDealAddress] = useState<string>('124 Pine Crest Rd, Austin, TX');
  
  // Calculate MAO
  const mao = useMemo(() => {
    const arvPortion = arv * (rulePercent / 100);
    return arvPortion - rehab - fee;
  }, [arv, rehab, fee, rulePercent]);

  // Calculations for breakdown
  const ruleLimit = useMemo(() => arv * (rulePercent / 100), [arv, rulePercent]);
  const totalCosts = useMemo(() => rehab + fee, [rehab, fee]);
  const maoToArvRatio = useMemo(() => (arv > 0 ? (mao / arv) * 100 : 0), [mao, arv]);

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Top Header */}
      <div className="flex items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center">
            <TrendingUp className="text-[var(--t-primary)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Deal Analyzer</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">Determine Maximum Allowable Offer (MAO) & evaluate wholesale margins</p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Calculator Inputs - Left 5 cols */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-bold text-[var(--t-text)] flex items-center gap-2">
                <Calculator size={18} className="text-[var(--t-primary)]" />
                Calculator Inputs
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-2">
                    Property Address / Name
                  </label>
                  <input
                    type="text"
                    value={dealAddress}
                    onChange={(e) => setDealAddress(e.target.value)}
                    className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                    placeholder="Enter address..."
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-2">
                    After Repair Value (ARV)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] font-bold text-sm">$</span>
                    <input
                      type="number"
                      value={arv || ''}
                      onChange={(e) => setArv(Number(e.target.value))}
                      className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)] font-semibold"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-2">
                      Estimated Rehab
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] font-bold text-sm">$</span>
                      <input
                        type="number"
                        value={rehab || ''}
                        onChange={(e) => setRehab(Number(e.target.value))}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)] font-semibold"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-2">
                      Wholesale Fee / Margin
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] font-bold text-sm">$</span>
                      <input
                        type="number"
                        value={fee || ''}
                        onChange={(e) => setFee(Number(e.target.value))}
                        className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)] font-semibold"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block">
                      ARV Percentage (Rule of {rulePercent}%)
                    </label>
                    <span className="text-sm font-bold text-[var(--t-primary)]">{rulePercent}%</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="90"
                    step="1"
                    value={rulePercent}
                    onChange={(e) => setRulePercent(Number(e.target.value))}
                    className="w-full accent-[var(--t-primary)] bg-[var(--t-border)] h-1 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between text-[9px] text-[var(--t-text-muted)] mt-1 font-mono">
                    <span>50%</span>
                    <span>70% (Standard)</span>
                    <span>90%</span>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-2">
                    Deal Notes / Considerations
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                    placeholder="Enter structural issues, neighborhood metrics..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Calculator Results - Right 7 cols */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* MAO Primary Card */}
            <div className="relative overflow-hidden rounded-3xl border border-[var(--t-border)] bg-[var(--t-surface)] p-8">
              {/* Premium Glow effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--t-primary)]/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-primary)]">
                    Maximum Allowable Offer
                  </span>
                  <h2 className="text-4xl font-black tracking-tight text-[var(--t-text)] mt-1">
                    ${mao.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                  </h2>
                  <p className="text-xs text-[var(--t-text-muted)] mt-2">
                    Offer above this limit will reduce your target wholesale fee.
                  </p>
                </div>
                
                <div className="shrink-0 bg-[var(--t-primary-dim)] px-4 py-3 rounded-2xl border border-[var(--t-primary)]/20">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[var(--t-primary)]">
                    MAO / ARV RATIO
                  </div>
                  <div className="text-lg font-black text-[var(--t-text)] mt-0.5">
                    {maoToArvRatio.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Progress visual representation */}
              <div className="mt-8 space-y-2">
                <div className="flex justify-between text-[10px] font-bold text-[var(--t-text-muted)]">
                  <span>MAO limit: ${mao.toLocaleString()}</span>
                  <span>ARV: ${arv.toLocaleString()}</span>
                </div>
                <div className="w-full bg-[var(--t-bg)] rounded-full h-3 overflow-hidden border border-[var(--t-border)]">
                  <div 
                    className="bg-gradient-to-r from-[var(--t-primary)] to-[var(--t-primary)]/80 h-full rounded-full transition-all duration-500 ease-out" 
                    style={{ width: `${Math.min(100, Math.max(0, maoToArvRatio))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Financial Breakdown Table */}
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6">
              <h3 className="text-sm font-black uppercase text-[var(--t-text)] tracking-wider mb-4">
                Financial Breakdown
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[var(--t-border)]/50 text-sm">
                  <span className="text-[var(--t-text-muted)]">After Repair Value (ARV)</span>
                  <span className="font-bold text-[var(--t-text)]">${arv.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[var(--t-border)]/50 text-sm">
                  <span className="text-[var(--t-text-muted)]">ARV Limit ({rulePercent}%)</span>
                  <span className="font-semibold text-[var(--t-text)]">${ruleLimit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[var(--t-border)]/50 text-sm">
                  <span className="text-[var(--t-text-muted)]">Less Estimated Rehab</span>
                  <span className="font-semibold text-[var(--t-error)]">-${rehab.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[var(--t-border)]/50 text-sm">
                  <span className="text-[var(--t-text-muted)]">Less Target Wholesale Fee</span>
                  <span className="font-semibold text-[var(--t-error)]">-${fee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 text-base">
                  <span className="font-bold text-[var(--t-text)]">Maximum Allowable Offer</span>
                  <span className="font-black text-[var(--t-primary)]">${mao.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* AI Insights & Safety Margins */}
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-black uppercase text-[var(--t-text)] tracking-wider flex items-center gap-2">
                <ShieldCheck size={16} className="text-[var(--t-success)]" />
                Deal Risk Assessment
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--t-bg)] rounded-xl border border-[var(--t-border)]">
                  <span className="text-[9px] font-black text-[var(--t-text-muted)] uppercase tracking-wider">
                    Total wholesale margin
                  </span>
                  <p className="text-lg font-black text-[var(--t-text)] mt-1">
                    ${fee.toLocaleString()}
                  </p>
                  <span className="text-[10px] text-[var(--t-success)] font-medium mt-1 block">
                    ✓ High margin wholesale opportunity
                  </span>
                </div>

                <div className="p-4 bg-[var(--t-bg)] rounded-xl border border-[var(--t-border)]">
                  <span className="text-[9px] font-black text-[var(--t-text-muted)] uppercase tracking-wider">
                    Rehab-to-ARV Ratio
                  </span>
                  <p className="text-lg font-black text-[var(--t-text)] mt-1">
                    {arv > 0 ? ((rehab / arv) * 100).toFixed(1) : '0'}%
                  </p>
                  <span className="text-[10px] text-[var(--t-warning)] font-medium mt-1 block">
                    ⚠️ Rehab is {(arv > 0 && (rehab / arv) > 0.15) ? 'high' : 'reasonable'} relative to value
                  </span>
                </div>
              </div>

              <div className="p-4 bg-[var(--t-primary-dim)]/20 border border-[var(--t-primary)]/20 rounded-xl">
                <h4 className="text-xs font-bold text-[var(--t-primary)] flex items-center gap-1">
                  💡 Pro-Tip
                </h4>
                <p className="text-xs text-[var(--t-text-muted)] mt-1 leading-relaxed">
                  Make your initial offer 5-10% below the MAO limit to give yourself room for seller counter-offers. Maintain a clear scope of work for the rehab to prevent cost overruns.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
